import { useEffect, useState } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { Layout, message, Modal, Button } from 'antd'
import Header from './components/Header'
import QueryPage from './pages/QueryPage'
import BrowsePage from './pages/BrowsePage'
import LoginPage from './pages/LoginPage'

import { apiService } from './services/api'
import { SystemInfo } from './types/api'
import { NetworkDiagnostic } from './utils/networkDiagnostic'

const { Content, Footer } = Layout

function App() {
  const [systemInfo, setSystemInfo] = useState<SystemInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  useEffect(() => {
    initializeApp()
  }, [])

  const initializeApp = async () => {
    try {
      // 检查是否有存储的token
      const token = localStorage.getItem('access_token')
      if (token) {
        setIsAuthenticated(true)

        // 只有在有token的情况下才获取系统信息
        try {
          const info = await apiService.getSystemInfo()
          setSystemInfo(info)
        } catch (error: any) {
          // 如果token无效，清除认证状态
          if (error.response?.status === 401) {
            localStorage.removeItem('access_token')
            localStorage.removeItem('user_info')
            setIsAuthenticated(false)
          }
        }
      } else {
        // 没有token时，尝试获取公开的系统信息
        try {
          const info = await apiService.getSystemInfo()
          setSystemInfo(info)
        } catch (error) {
          console.log('无法获取系统信息，可能需要登录')
        }
      }

      // 测试系统健康状态（公开接口）
      try {
        const health = await apiService.healthCheck()
        if (health.status === 'healthy') {
          message.success('系统连接正常')
        } else {
          message.warning('系统状态异常')
        }
      } catch (error: any) {
        console.log('健康检查失败:', error)

        // If health check fails with SSL error, show diagnostic modal
        if (error.message?.includes('SSL') || error.code === 'ERR_SSL_PROTOCOL_ERROR' || error.code === 'ERR_NETWORK') {
          showNetworkDiagnostic(error)
        }
      }
    } catch (error) {
      console.error('初始化应用失败:', error)
      message.error('系统初始化失败，请检查网络连接')
    } finally {
      setLoading(false)
    }
  }

  const handleLogin = (token: string) => {
    localStorage.setItem('access_token', token)
    setIsAuthenticated(true)
    message.success('登录成功')
  }

  const handleLogout = () => {
    localStorage.removeItem('access_token')
    localStorage.removeItem('user_info')
    setIsAuthenticated(false)
    message.success('已退出登录')
  }

  const showNetworkDiagnostic = async (error: any) => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || '/api'

    Modal.error({
      title: '网络连接问题',
      width: 600,
      content: (
        <div>
          <p><strong>检测到SSL/网络连接错误：</strong></p>
          <p style={{ color: '#ff4d4f', fontFamily: 'monospace', fontSize: '12px' }}>
            {error.message || error.code || '未知错误'}
          </p>

          <p><strong>可能的原因：</strong></p>
          <ul>
            <li>网络代理或防火墙阻止了HTTPS连接</li>
            <li>SSL证书验证失败</li>
            <li>网络连接不稳定</li>
          </ul>

          <p><strong>建议解决方案：</strong></p>
          <ul>
            <li>检查网络连接是否正常</li>
            <li>暂时关闭VPN或代理</li>
            <li>尝试刷新页面</li>
            <li>联系系统管理员</li>
          </ul>
        </div>
      ),
      footer: [
        <Button key="diagnose" onClick={async () => {
          Modal.destroyAll()
          const diagnostic = await NetworkDiagnostic.diagnoseAPIConnectivity(baseUrl)

          Modal.info({
            title: '网络诊断结果',
            width: 700,
            content: (
              <div>
                <h4>连接测试结果：</h4>
                {diagnostic.results.map((result, index) => (
                  <div key={index} style={{ marginBottom: '8px' }}>
                    <span style={{ color: result.success ? '#52c41a' : '#ff4d4f' }}>
                      {result.success ? '✓' : '✗'}
                    </span>
                    <span style={{ marginLeft: '8px' }}>{result.endpoint}</span>
                    {result.error && (
                      <div style={{ marginLeft: '20px', fontSize: '12px', color: '#666' }}>
                        错误: {result.error}
                      </div>
                    )}
                  </div>
                ))}

                <h4 style={{ marginTop: '16px' }}>建议：</h4>
                <ul>
                  {diagnostic.recommendations.map((rec, index) => (
                    <li key={index}>{rec}</li>
                  ))}
                </ul>
              </div>
            )
          })
        }}>
          运行网络诊断
        </Button>,
        <Button key="close" type="primary" onClick={() => Modal.destroyAll()}>
          关闭
        </Button>
      ]
    })
  }

  if (loading) {
    return (
      <Layout className="app-layout">
        <Content className="app-content">
          <div className="loading-container">
            <div>正在初始化系统...</div>
          </div>
        </Content>
      </Layout>
    )
  }

  return (
    <Layout className="app-layout">
      <Header 
        systemInfo={systemInfo}
        isAuthenticated={isAuthenticated}
        onLogout={handleLogout}
      />
      
      <Content className="app-content">
        <Routes>
          <Route
            path="/login"
            element={
              isAuthenticated ?
                <Navigate to="/query" replace /> :
                <LoginPage onLogin={handleLogin} />
            }
          />
          <Route
            path="/"
            element={
              isAuthenticated ?
                <Navigate to="/query" replace /> :
                <Navigate to="/login" replace />
            }
          />
          <Route
            path="/query"
            element={
              isAuthenticated ?
                <QueryPage systemInfo={systemInfo} /> :
                <Navigate to="/login" replace />
            }
          />
          <Route
            path="/browse"
            element={
              isAuthenticated ?
                <BrowsePage systemInfo={systemInfo} /> :
                <Navigate to="/login" replace />
            }
          />

          <Route
            path="*"
            element={<Navigate to="/query" replace />}
          />
        </Routes>
      </Content>

      <Footer className="app-footer">
        成本分析系统 ©2024 - 基于 AI 的智能查询平台
        {systemInfo?.ai_model && (
          <span style={{ marginLeft: 16, color: '#666' }}>
            AI模型: {systemInfo.ai_model.name} 
            {systemInfo.ai_model.simulated && ' (模拟模式)'}
          </span>
        )}
      </Footer>
    </Layout>
  )
}

export default App
